import { useState, useEffect } from 'react';
import { useDataProvider } from '@refinedev/core';

interface ClinicDues {
    total_due: number;
    sales_dues: number;
    invoice_dues: number;
    currency?: string;
}

export const useClinicDues = (patientId: string | number | null) => {
    const [dues, setDues] = useState<ClinicDues | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const getDataProvider = useDataProvider();

    useEffect(() => {
        const fetchClinicDues = async () => {
            if (!patientId) {
                setDues(null);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const dataProvider = getDataProvider();
                if (dataProvider && dataProvider.custom) {
                    // Fetch both clinic dues endpoints
                    const [salesResponse, invoiceResponse] = await Promise.all([
                        dataProvider.custom({
                            url: `/sales/clinic-dues/${patientId}`,
                            method: 'get',
                        }),
                        dataProvider.custom({
                            url: `/clinic-invoices/clinic-dues/${patientId}`,
                            method: 'get',
                        })
                    ]);

                    // Extract dues from both responses
                    const salesDues = salesResponse.data?.data?.sum_of_dues ||
                        salesResponse.data?.sum_of_dues || 0;

                    const invoiceDues = invoiceResponse.data?.data?.sum_of_dues ||
                        invoiceResponse.data?.sum_of_dues || 0;


                    // Calculate total clinic dues
                    const totalDues = +salesDues + +invoiceDues;

                    setDues({
                        total_due: totalDues,
                        sales_dues: salesDues,
                        invoice_dues: invoiceDues,
                        currency: 'INR'
                    });
                } else {
                    throw new Error('DataProvider custom method not available');
                }
            } catch (err: any) {
                console.error('Error fetching clinic dues:', err);
                setError(err?.response?.data?.message || 'Failed to fetch clinic dues');
                setDues({
                    total_due: 0,
                    sales_dues: 0,
                    invoice_dues: 0
                });
            } finally {
                setLoading(false);
            }
        };

        fetchClinicDues();
    }, [patientId, getDataProvider]);

    return { dues, loading, error };
};
